"use client";
import React from "react";
import Image, { type StaticImageData } from "next/image";

type ServiceItem = {
  title: React.ReactNode;
  description: string;
  icon?: string | StaticImageData; // path/URL or imported static image
  categories: string[];
};

interface AiDevelopmentServicesProps {
  title: React.ReactNode;
  description: string;
  subtitle: string;
  servicesArray: ServiceItem[];
}

const AiDevelopmentServices = ({
  title,
  description,
  subtitle,
  servicesArray,
}: AiDevelopmentServicesProps) => {
  return (
    <section className="relative w-full overflow-hidden bg-white px-4 py-16 sm:px-6 md:px-10 lg:px-[100px] lg:py-[100px]">
      <div className="mx-auto w-full max-w-[5000px]">
        {/* Header Section */}
        <h3 className="text-sm font-medium uppercase leading-[140%] text-[#00040b] sm:text-base mb-5">
          {subtitle}
        </h3>
        <div className="mb-12 flex flex-col gap-6 lg:mb-[54px] lg:flex-row lg:items-start lg:gap-[30px] justify-between">
          <div className="flex flex-col gap-4 lg:max-w-[845px]">
            <h2 className="clash-font text-[clamp(2.5rem,2.2rem+1.5vw,4.5rem)] font-medium leading-[120%] text-[#00040b] lg:max-w-[800px]">
              {title}
            </h2>
          </div>
          <p className="text-base font-normal leading-[160%] text-[#00040b]/60 sm:text-xl lg:max-w-[845px]">
            {description}
          </p>
        </div>

        {/* Separator Line */}
        <div className="mb-12 h-px w-full bg-[#00040b]/10 lg:mb-[54px]"></div>

        {/* Services List */}
        <div className="w-full lg:ml-[146px] lg:w-[1428px]">
          <div className="flex flex-col gap-10 lg:gap-[44px]">
            {servicesArray?.map((service, index) => (
              <div key={index} className="flex flex-col">
                <div className="flex flex-col gap-6 sm:px-4 sm:py-4 lg:flex-row lg:items-center lg:gap-[54px] lg:px-0 lg:py-0">
                  {/* Icon */}
                  <div className="flex items-center gap-4 sm:gap-6 lg:h-[154px] lg:w-[160px] lg:justify-center lg:gap-0">
                    <div className="ml-0 flex items-center justify-center lg:ml-[53px] lg:h-[100px] lg:w-[100px]">
                      {service.icon && (
                        <Image
                          src={service.icon}
                          alt=""
                          width={90}
                          height={90}
                          className="h-[70px] w-[70px] object-contain lg:h-[90px] lg:w-[90px]"
                        />
                      )}
                    </div>
                  </div>

                  {/* Vertical Separator */}
                  <div className="hidden h-[154px] w-px bg-[#00040b]/10 lg:block"></div>

                  {/* Content */}
                  <div className="flex w-full flex-col gap-0 lg:w-[1160px]">
                    <h3 className="clash-font text-[clamp(1.875rem,1.5rem+1vw,2.375rem)] font-medium leading-[120%] text-[#00040b]">
                      {service.title}
                    </h3>

                    {/* Categories / Chips */}
                    <div className="tags-container gap-[10px] py-[15px] pb-[20px] flex-wrap lg:flex-nowrap flex lg:gap-0 lg:[&_.tag-item:first-child]:rounded-l-[50px] lg:[&_.tag-item:first-child]:pl-[30px] lg:[&_.tag-item:last-child]:rounded-r-[50px] lg:[&_.tag-item:last-child]:pr-[30px]">
                      {service.categories?.map((cat, catIndex) => (
                        <React.Fragment key={catIndex}>
                          <div className="tag-item bg-[#f3f5f8] flex px-3 rounded-2xl lg:px-5 lg:rounded-[0px] items-center justify-center py-2 lg:py-0 lg:h-[40px] text-[#00040BCC] font-[600] text-xs md:text-sm text-center">
                            {cat}
                          </div>

                          {catIndex < service.categories.length - 1 && (
                            <div className="hidden lg:block h-[40px] w-[3px] flex bg-white self-center" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>

                    <p
                      className="text-base font-normal leading-[160%] text-[#00040b]/60 sm:text-lg"
                      dangerouslySetInnerHTML={{
                        __html: service.description ?? "",
                      }}
                    />
                  </div>
                </div>

                {/* Horizontal Separator (except for last item) */}
                {index < servicesArray.length - 1 && (
                  <div className="mt-8 h-px w-full bg-[#00040b]/10 lg:h-[1px]"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AiDevelopmentServices;
